import { useQuery } from "@tanstack/react-query";
import {
  Calendar,
  ChevronDown,
  ChevronRight,
  MessageSquare,
  Pin,
  Plus,
  RefreshCw,
  Search,
  Send,
  Users,
} from "lucide-react";
import { useState } from "react";
import { useLocation } from "wouter";
import { PageHeader } from "@/components/page-header";
import { StatusBadge } from "@/components/status-badge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { formatDate, formatDateTime } from "@/lib/constants";
import { orpc } from "@/lib/rpc";
import { matchesSearch } from "@/lib/search-utils";

export function AdminSurveysView() {
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedSurveys, setExpandedSurveys] = useState<Set<string>>(new Set());
  const [, setLocation] = useLocation();

  const { data: surveys, isLoading } = useQuery(orpc.pulseSurveys.list.queryOptions());

  const toggleExpanded = (surveyId: string) => {
    const newExpanded = new Set(expandedSurveys);
    if (newExpanded.has(surveyId)) {
      newExpanded.delete(surveyId);
    } else {
      newExpanded.add(surveyId);
    }
    setExpandedSurveys(newExpanded);
  };

  const getSurveyTypeBadge = (type: string) => {
    switch (type) {
      case "recurring":
        return (
          <Badge variant="secondary" className="gap-1">
            <RefreshCw className="h-3 w-3" />
            Recorrente
          </Badge>
        );
      default:
        return (
          <Badge variant="secondary" className="gap-1">
            <Pin className="h-3 w-3" />
            Pontual
          </Badge>
        );
    }
  };

  const getSurveyDescription = (survey: any) => {
    if (survey.surveyType === "recurring") {
      return `A cada ${survey.recurrenceInDays} dias`;
    }
    return "";
  };

  const _getResponseRate = (survey: any) => {
    const totalExpected = (survey as any).totalExpected || 0;
    const finishedCount = (survey as any).finishedCount || 0;
    return totalExpected > 0 ? Math.round((finishedCount / totalExpected) * 100) : 0;
  };

  const filteredSurveys =
    surveys?.filter((survey) => matchesSearch(survey.title, searchQuery)) ?? [];

  const adhocCount = surveys?.filter((s) => s.surveyType === "adhoc").length || 0;
  const recurringCount = surveys?.filter((s) => s.surveyType === "recurring").length || 0;
  const totalInstances = surveys?.reduce((sum, s) => sum + (s.instanceCount || 0), 0) || 0;

  return (
    <div className="flex-1 space-y-6">
      <PageHeader
        title="Pesquisas de Clima"
        description="Crie e gerencie pesquisas de clima organizacional"
      >
        <Button onClick={() => setLocation("/surveys/new")} data-testid="button-add-survey">
          <Plus className="h-4 w-4 mr-2" />
          Nova Pesquisa
        </Button>
      </PageHeader>

      <div className="grid gap-4 md:grid-cols-3">
        <Card className="bg-[hsl(var(--paper-white))] border-[hsl(var(--sand-divider))]">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="rounded-lg bg-[hsl(var(--sand-step))] p-3">
              <MessageSquare className="h-5 w-5 text-[hsl(var(--ink-muted))]" />
            </div>
            <div>
              <p className="text-2xl font-semibold tabular-nums text-[hsl(var(--ink-primary))]">
                {adhocCount}
              </p>
              <p className="text-sm text-[hsl(var(--ink-muted))]">Pontuais</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[hsl(var(--paper-white))] border-[hsl(var(--sand-divider))]">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="rounded-lg bg-[hsl(var(--sand-step))] p-3">
              <Send className="h-5 w-5 text-[hsl(var(--forest-pulse))]" />
            </div>
            <div>
              <p className="text-2xl font-semibold tabular-nums text-[hsl(var(--ink-primary))]">
                {recurringCount}
              </p>
              <p className="text-sm text-[hsl(var(--ink-muted))]">Recorrentes</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[hsl(var(--paper-white))] border-[hsl(var(--sand-divider))]">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="rounded-lg bg-[hsl(var(--sand-step))] p-3">
              <Users className="h-5 w-5 text-[hsl(var(--ink-primary))]" />
            </div>
            <div>
              <p className="text-2xl font-semibold tabular-nums text-[hsl(var(--ink-primary))]">
                {totalInstances}
              </p>
              <p className="text-sm text-[hsl(var(--ink-muted))]">Instâncias</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar pesquisas..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
            data-testid="input-search-surveys"
          />
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-12 text-[hsl(var(--ink-muted))]">Carregando…</div>
      ) : filteredSurveys.length === 0 ? (
        <div className="text-center py-12 text-[hsl(var(--ink-muted))]">
          {searchQuery ? "Nenhuma pesquisa encontrada" : "Crie sua primeira pesquisa."}
        </div>
      ) : (
        <div className="space-y-4">
          {filteredSurveys.map((survey) => (
            <Card
              key={survey.id}
              className="overflow-visible cursor-pointer bg-[hsl(var(--paper-white))] border-[hsl(var(--sand-divider))] rounded-md transition-colors hover:bg-[hsl(var(--sand-step))]"
              onClick={() => setLocation(`/surveys/${survey.id}`)}
              data-testid={`card-survey-${survey.id}`}
            >
              <div className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <h3 className="font-semibold text-lg">{survey.title}</h3>
                      {getSurveyTypeBadge(survey.surveyType)}
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">
                      {survey.questions?.length || 0} perguntas{" "}
                      {getSurveyDescription(survey) && `\u2022 ${getSurveyDescription(survey)}`}
                    </p>
                    {survey.description && (
                      <p className="text-sm text-muted-foreground">{survey.description}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {survey.instances && survey.instances.length > 0 && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleExpanded(survey.id);
                        }}
                        data-testid={`button-toggle-instances-${survey.id}`}
                      >
                        {expandedSurveys.has(survey.id) ? (
                          <>
                            <ChevronDown className="h-4 w-4 mr-1" />
                            Ocultar Instancias
                          </>
                        ) : (
                          <>
                            <ChevronRight className="h-4 w-4 mr-1" />
                            Ver Instancias ({survey.instances.length})
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </div>

                {expandedSurveys.has(survey.id) &&
                  survey.instances &&
                  survey.instances.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-[hsl(var(--sand-divider))] space-y-2">
                      <h4 className="text-[11px] uppercase font-semibold text-[hsl(var(--ink-muted))] tracking-[0.02em] mb-3">
                        Instâncias enviadas
                      </h4>
                      {survey.instances.map((instance: any) => (
                        // biome-ignore lint/a11y/useSemanticElements: interactive container with nested interactive elements cannot be a native button
                        <div
                          key={instance.id}
                          className="flex items-center justify-between gap-3 p-3 rounded-md cursor-pointer transition-colors hover:bg-[hsl(var(--sand-step))]"
                          role="button"
                          tabIndex={0}
                          onClick={(e) => {
                            e.stopPropagation();
                            setLocation(`/surveys/instances/${instance.id}`);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              e.stopPropagation();
                              setLocation(`/surveys/instances/${instance.id}`);
                            }
                          }}
                          data-testid={`instance-row-${instance.id}`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Calendar className="h-4 w-4 text-[hsl(var(--ink-muted))] shrink-0" />
                              <span className="text-sm font-medium text-[hsl(var(--ink-primary))]">
                                Enviado em {formatDateTime(instance.sentAt)}
                              </span>
                              <StatusBadge status={instance.status} />
                            </div>
                            <div className="flex items-center gap-3 mt-1">
                              <p className="text-xs text-[hsl(var(--ink-muted))]">
                                Prazo: {formatDate(instance.deadline)}
                              </p>
                              {instance.totalResponses !== undefined && (
                                <span
                                  className="text-xs text-[hsl(var(--ink-muted))] tabular-nums"
                                  data-testid={`instance-rate-${instance.id}`}
                                >
                                  {instance.finishedResponses}/{instance.totalResponses} responderam
                                  ({instance.responseRate}%)
                                </span>
                              )}
                            </div>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              setLocation(`/surveys/instances/${instance.id}`);
                            }}
                          >
                            Ver Respostas
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
